// config/apiEndpoints.js - Centralized API endpoints configuration

export const API_ENDPOINTS = {
  // Authentication endpoints
  AUTH: {
    LOGIN: "/api/UserLogin/User_Login",
    LOGOUT: "/api/UserLogin/User_Logout",
    VALIDATE_SESSION: "/api/UserLogin/ValidateSession",
    REFRESH_TOKEN: "/api/UserLogin/RefreshToken",
    CHECK_EMAIL_ID: "/api/UserLogin/CheckEmailId",
    VALIDATE_OTP: "/api/UserLogin/ValidateOTP",
    SET_NEW_PASSWORD: "/api/UserLogin/SetNewPassword",
  },

  // User management endpoints
  USER: {
    PROFILE: "/api/User/Profile",
    UPDATE_PROFILE: "/api/User/UpdateProfile",
    CHANGE_PASSWORD: "/api/UserLogin/ChangePwd",
    CHANGE_ACCESS_CODE: "/api/UserLogin/ChangeAccessCode",
    GET_PERMISSIONS: "/api/User/GetPermissions",
    GET_USERLIST: "/api/eDelegation/UsersActivity",
    GET_USER_PROFILE_COMMONDATA: "/api/eDelegation/GetPageRecordsByPageName",
    GET_INDIVIDUAL_USER: "/api/eDelegation/GetIndividualData",
    GET_EXTERNAL_ENTITY_ACCESS: "/api/eDelegation/GetExternalEntityAccess",
    SAVE_UPDATE_USER: "/api/eDelegation/StaffRegistrationNew",
    ACTIVATE_DEACTIVATE_USER: "/api/eDelegation/UsersActivity",
    SAVE_ESIGNATURE: "/api/eDelegation/UsersActivity", // Separate API to save e-signature (base64) to DB//UpdateSignature
  },

  ROLES: {
    StaffRolesActivity: "/api/Settings/StaffRolesActivity",
  },

  /** Legacy DMS — QMS uses /api/QMS/*; eISF uses /api/eISF/* document masters */
  DMS: {
    DOCUMENT_TYPE_ACTIVITY: "/api/DMS/DocumentTypeActivity",
  },

  /**
   * QMS (clone of DMS API — ClinicalTrialAPI QMSController → DMSSetUp).
   * React UI should use QMS.* endpoints only.
   */
  QMS: {
    DOCUMENT_TYPE_ACTIVITY: "/api/QMS/DocumentTypeActivity",
    DOCUMENT_CATEGORY_ACTIVITY: "/api/QMS/DocumentCategoryActivity",
    MY_VAULT_ACTIVITY: "/api/QMS/DMS_MyVaultActivity",
    MY_VAULT_UPLOAD: "/api/QMS/DMS_MyVaultUploadDocument",
    SHARED_WITH_ME_ACTIVITY: "/api/QMS/DMS_SharedWithMeActivity",
    DOCUMENT_COMMENT_ACTIVITY: "/api/QMS/DMS_DocumentCommentActivity",
    CHECK_IN_CHECK_OUT_ACTIVITY: "/api/QMS/DMS_DocumentCheckInCheckOutActivity",
    DOCUMENT_QUIZZ_ACTIVITY: "/api/QMS/DMS_DocumentQuizzActivity",
    MY_LEARNING_ACTIVITY: "/api/QMS/DMS_MyLearningActivity",
    DASHBOARD_ACTIVITY: "/api/QMS/DashboardActivity",
    CHANGE_REQUEST_ACTIVITY: "/api/QMS/DocumentChangeRequestActivity",
    TRACKER_ACTIVITY: "/api/QMS/DMSTrackerActivity",
    CERTIFICATE_TEMPLATE_ACTIVITY: "/api/QMS/DMSCertificatetemplateActivity",
    DOCUMENT_LIBRARY_V2_ACTIVITY: "/api/QMS/DocumentLibraryV2Activity",
    MY_LEARNING_V2_ACTIVITY: "/api/QMS/MyLearningV2Activity",
    QUESTION_BANK_V2_ACTIVITY: "/api/QMS/QuestionBankV2Activity",
    QUIZ_V2_ACTIVITY: "/api/QMS/QuizV2Activity",
    CONFIG_GET: "/api/QMS/GetConfig",
    CONFIG_SAVE: "/api/QMS/SaveConfig",
  },

  // Site management (Tbl_OrganizationSite / Usp_OrganizationSiteActivity_NEWUI)
  SITE: {
    SITE_ACTIVITY: "/api/Site/SiteActivity",
  },

  /** Physical site / building / floor / room capacity (Admin) */
  CAPACITY_MANAGEMENT: {
    ACTIVITY: "/api/CapacityManagement/CapacityManagementActivity",
  },

  ORG_SECURITY: {
    GET_LOGIN_IPS: "/api/Settings/GetOrganizationLoginIps",
    LOGIN_IP_ACTIVITY: "/api/Settings/OrganizationLoginIpActivity",
    GET_STAFF_LOGIN_IPS: "/api/Settings/GetStaffLoginIps",
    STAFF_LOGIN_IP_ACTIVITY: "/api/Settings/StaffLoginIpActivity",
    GET_SETUP_CLIENT_IP: "/api/Settings/GetSetupClientIp",
  },

  CREDENTIALS: {
    GET_ORG_POLICY: "/api/Settings/GetOrganizationCredentialPolicy",
    ORG_POLICY_ACTIVITY: "/api/Settings/OrganizationCredentialPolicyActivity",
    GET_STAFF_STATUS: "/api/UserLogin/GetStaffCredentialStatus",
    ACCEPT_TERMS: "/api/UserLogin/SaveTermsAndConditionsFlag",
    CHANGE_PASSWORD: "/api/UserLogin/ChangePwd",
    CHANGE_ACCESS_CODE: "/api/UserLogin/ChangeAccessCode",
  },

  REMINDERS: {
    GET_ORG_SETTINGS: "/api/Settings/GetOrganizationReminderSettings",
    ORG_SETTINGS_ACTIVITY: "/api/Settings/OrganizationReminderSettingsActivity",
  },

  ORGANIZATION: {
    SETUP_ACTIVITY: "/api/Settings/OrganizationSetUpActivity",
    LOCATION_ACTIVITY: "/api/Settings/LocationSetUpActivity",
    STAFF_DOCUMENT: "/api/Settings/SaveStaffDocument",
    CHANGE_ORGANIZATION: "/api/Settings/ChangeOrganization",
    /** AQ Super Admin — list all customer organisations */
    GET_ORGANIZATIONS_DETAILS: "/api/Settings/GetOrganizationsDetails",
    SUPER_ADMIN_QMS_TRAINING_FLAG: "/api/Settings/SuperAdminOrgQmsTrainingFlag",
    SUPER_ADMIN_SPONSOR_CRO_MANAGEMENT_FLAG:
      "/api/Settings/SuperAdminOrgSponsorCroManagementFlag",
    SUPER_ADMIN_SPONSOR_CRO_STUDY_ACCESS_EMAIL_TEMPLATE:
      "/api/Settings/SuperAdminSponsorCroStudyAccessEmailTemplate",
    SUPER_ADMIN_MFA_FLAG: "/api/Settings/SuperAdminOrgMfaFlag",
    SUPER_ADMIN_MULTIPLE_REMINDERS_FLAG: "/api/Settings/SuperAdminOrgMultipleRemindersFlag",
    SUPER_ADMIN_FAQ_AI_FLAG: "/api/Faq/SuperAdminOrgFaqAiFlag",
    SUPER_ADMIN_FAQ_AI_PLATFORM_SETTINGS: "/api/Faq/SuperAdminFaqAiPlatformSettings",
    GET_FAQ_AI_USAGE: "/api/Faq/GetFaqAiUsage",
    ORG_MESSAGING_SETTINGS: "/api/Settings/OrgMessagingSettings",
    GET_TWILIO_OUTBOUND_MESSAGES: "/api/Settings/GetTwilioOutboundMessages",
    CREATE_ORGANIZATION: "/api/Settings/CreateOrganization",
  },

  SETTINGS: {
    /** Org-scoped Department Manager (Admin > Departments) */
    DEPARTMENT_MANAGER: "/api/Settings/DepartmentManager",
    /** Send Bulk Email/SMS — filter recipients + send (legacy SettingController) */
    STUDY_WISE_VOLUNTEERS: "/api/Settings/StudyWiseVolunteers",
    SEND_EMAIL_SMS: "/api/Settings/SendEmailSMS",
  },

  /** Platform FAQ + FAQ AI — FaqController (not Settings) */
  FAQ: {
    GET_PLATFORM_FAQS: "/api/Faq/GetPlatformFaqs",
    SAVE_PLATFORM_FAQ: "/api/Faq/SavePlatformFaq",
    DELETE_PLATFORM_FAQ: "/api/Faq/DeletePlatformFaq",
    DELETE_ALL_PLATFORM_FAQS: "/api/Faq/DeleteAllPlatformFaqs",
    IMPORT_PLATFORM_FAQS: "/api/Faq/ImportPlatformFaqs",
    GET_USER_GUIDES: "/api/Faq/GetPlatformUserGuides",
    SAVE_USER_GUIDE: "/api/Faq/SavePlatformUserGuide",
    DELETE_USER_GUIDE: "/api/Faq/DeletePlatformUserGuide",
    GET_USER_GUIDE_FILE: "/api/Faq/GetPlatformUserGuideFile",
    REORDER_USER_GUIDES: "/api/Faq/ReorderPlatformUserGuides",
    GET_FAQ_AI_STATUS: "/api/Faq/GetFaqAiStatus",
    ASK_PLATFORM_FAQ: "/api/Faq/AskPlatformFaq",
    GET_FAQ_AI_CHAT_HISTORY: "/api/Faq/GetFaqAiChatHistory",
    GET_FAQ_AI_CHAT_SESSION: "/api/Faq/GetFaqAiChatSession",
    SET_FAQ_AI_FEEDBACK: "/api/Faq/SetFaqAiFeedback",
  },


  /** Lookup / prepopulated data for dropdowns (Usp_LookupData_NEWUI) */
  LOOKUP: {
    LOOKUP_ACTIVITY: "/api/Lookup/LookupActivity",
  },

  // Study management endpoints
  STUDY: {
    GET_ALL_STUDIES: "/api/eSchedule/ScheduleStudyDetails",
    GET_STUDY_MGMT_LIST: "/api/eSchedule/GetStudyMgmtList",
    GET_BY_ID: "/api/Study/GetById",
    CREATE: "/api/Study/Create",
    UPDATE: "/api/Study/Update",
    DELETE: "/api/Study/Delete",
    /** Add/Update study (legacy form parity with Add Study modal) */
    STUDY_ACTIVITY: "/api/Settings/StudyActivity",
  },

  /** eSchedule cohort wizard + schedule setup (ClinicalTrialAPI eScheduleCohortController) */
  ESCHEDULE: {
    GET_COHORT_SETUP: "/api/eScheduleCohort/GetStudyScheduleSetUpCohort",
    SINGLE_GROUP_SETUP: "/api/eScheduleCohort/CohortSingleGroupScheduleSetUp",
    MULTI_GROUP_STEP1: "/api/eScheduleCohort/MultipleGroupStep1",
    MULTI_GROUP_RUNIN: "/api/eScheduleCohort/MultipleGroupStep1RunIn",
    MULTI_GROUP_STEP2: "/api/eScheduleCohort/MultipleGroupStep2",
    MULTI_GROUP_STEP3: "/api/eScheduleCohort/MultipleGroupStep3ApplyVisit",
    GET_GROUP_INFO: "/api/eScheduleCohort/GetGroupInfo",
    GET_AUDIT_LOG: "/api/eScheduleCohort/GetScheduleAuditLog",
    WRITE_AUDIT_LOG: "/api/eScheduleCohort/WriteScheduleAuditLog",
    SCHEDULE_LIVE: "/api/eScheduleCohort/CohortScheduleLive",
    SAVE_UPDATE_DELETE_GROUP: "/api/eScheduleCohort/SaveUpdateDeleteGroup",
    CLONE_SCHEDULE_TO_STUDY: "/api/eScheduleCohort/CloneCohortScheduleToStudy",
    /** Per-visit detail save (legacy ESchedule parity) */
    INDIVIDUAL_VISIT: "/api/eSchedule/IndividualVisitDetails",
    CHANGE_VISIT_NAME: "/api/eSchedule/ChangeVisitName",
    SWAP_VISIT_ORDER: "/api/eSchedule/SwapVisitOrder",
    SCREENING_DETAILS: "/api/eSchedule/ScreeningDetails",
    VISITS_PER_STUDY: "/api/eSchedule/VisitsPerStudyDetails",
    SAVE_ESCHEDULE: "/api/eSchedule/SaveESchedule",
    /** Planner / bulk comm study dropdown (GetPlannerstudyListdata) */
    GET_STUDY_SCHEDULE_DATA: "/api/eSchedule/GetStudyScheduledata",
    /** Settings APIs used per visit (legacy parity) */
    SAVE_ECOMM_SETUP: "/api/Settings/SaveECommSetup",
    DEPARTMENT_ROLE: "/api/Settings/DepartmentRoleAssignAcivity",
    STUDY_WORKSHEET: "/api/Settings/StudyWorkSheetActivity",
  },

  /** ePlanner — appointments, calendar, occupancy (ClinicalTrialAPI ePlannerController) */
  EPLANNER: {
    SET_VISIT_PLANNER: "/api/ePlanner/SetVisitPlanner",
    GET_APPOINTMENT: "/api/ePlanner/GetAppointment",
    GET_APPOINTMENT_FAST: "/api/ePlanner/GetAppointmentFast",
    GET_APPOINTMENTS_DATA: "/api/ePlanner/GetAppointmentsdata",
    APPOINTMENT_ACTIVITY: "/api/ePlanner/AppointmentActivity",
    APPOINTMENT_ACTIVITY_NEW: "/api/ePlanner/AppointmentActivityNew",
    GET_CALENDAR_VIEW: "/api/ePlanner/GetCalendarViewAPI",
    GET_INPUT_BASED_VOL_DETAILS: "/api/ePlanner/GetInputBasedVolDetails",
    VIDEO_CONSULTATION: "/api/ePlanner/VideoConsultation",
    VIDEO_CONSULTATION_INVITATION: "/api/ePlanner/VideoConsultationInvitation",
    INVITE: "/api/ePlanner/Invite",
    SEARCH_VOLUNTEER: "/api/ePlanner/SearchVolunteer",
    APPOINTMENT_LOCATION_ACTIVITY: "/api/ePlanner/AppointmentLocationActivity",
    GET_STUDY_LOCATION_APPOINTMENTS: "/api/ePlanner/GetStudyLocationAppointments",
    GET_LOCATION_OCCUPANCY_DATA: "/api/ePlanner/GetLocationOccupancyData",
    GET_OCCUPANCY_BY_ROOM: "/api/ePlanner/GetOccupancyDetailsByRoomId",
    GET_EXCEL_DATA_OF_LOCATION: "/api/ePlanner/GetExcelDataOfLocation",
    GET_UNOCCUPIED_CAPACITY_DATA: "/api/ePlanner/GetUnOccupiedCapacityData",
    /** Analytics reports (same SPs as legacy Settings analytics) */
    GET_ANALYTICS: "/api/ePlanner/GetAnalytics",
    STUDY_WISE_APPOINTMENT: "/api/ePlanner/StudyWiseAppointment",
    GET_OW_APP_ANALYTICS: "/api/ePlanner/GetOWAppAnalytics",
    LOCATION_OCCUPANCY_ANALYTICS: "/api/ePlanner/LocationOccupancyAnalytics",
    /** WhatsApp two-way Communication inbox */
    LIST_WHATSAPP_THREADS: "/api/ePlanner/ListWhatsAppThreads",
    GET_WHATSAPP_THREAD: "/api/ePlanner/GetWhatsAppThread",
    REPLY_WHATSAPP: "/api/ePlanner/ReplyWhatsApp",
    /** Study / location / group / visit filters for planner screens */
    PLANNER_STUDY_BUNDLE: "/api/eSchedule/ScheduleStudyDetails",
  },

  /** eRecruitment — Participant Manager + E-Planner volunteer lookup */
  ERECRUITMENT: {
    SAVE_VOLUNTEER: "/api/eRecruitment/SaveVolunteer",
    SAVE_VOLUNTEER_DOCUMENT: "/api/eRecruitment/SaveVolunteerDocument",
    REPAIR_VOLUNTEER_DOCUMENTS: "/api/eRecruitment/RepairVolunteerDocuments",
    SEND_SMS_EMAIL_TO_VOLUNTEER: "/api/eRecruitment/SendSMSEmailToVolunteer",
    SEND_SMS_EMAIL_TO_TEST_USER: "/api/eRecruitment/SendSMSEmailToTestUser",
    VOLUNTEER_ACTIVITY: "/api/eRecruitment/VolunteerActivity",
    ETHNICITY: "/api/eRecruitment/Ethnicity",
    CLINICAL_DETAILS_PER_VOL: "/api/eRecruitment/ClinicalDetailsPerVol",
    SAVE_MEDICAL_HISTORY: "/api/eRecruitment/SaveMedicalHistory",
    GET_MEDICAL_HISTORY: "/api/eRecruitment/GetMedicalHistory",
    SAVE_SURGICAL_HISTORY: "/api/eRecruitment/SaveSurgicalHistory",
    GET_SURGICAL_HISTORY: "/api/eRecruitment/GetSurgicalHistory",
    SEARCH_SNOMED: "/api/eRecruitment/SearchSnomed",
    SNOMED_FILTER: "/api/eRecruitment/SnomedFilter",
    SAVE_FAMILY_HISTORY: "/api/eRecruitment/SaveFamilyHistory",
    GET_FAMILY_HISTORY: "/api/eRecruitment/GetFamilyHistory",
    SAVE_ALLERGY_HISTORY: "/api/eRecruitment/SaveAllergyHistory",
    GET_ALLERGY_HISTORY: "/api/eRecruitment/GetAllergyHistory",
    SAVE_PHYSICAL_MEASUREMENT: "/api/eRecruitment/SavePhysicalMeasurementHistory",
    GET_PHYSICAL_MEASUREMENT: "/api/eRecruitment/GetPhysicalMeasurementHistory",
    MEDICATION_HISTORY: "/api/eRecruitment/MedicationHistory",
    GET_MEDICATION_HISTORY: "/api/eRecruitment/GetMedicationHistory",
    TEMPLATE_SETUP: "/api/eRecruitment/TemplateSetupAPI",
    PARTICIPANTS_INFO: "/api/eRecruitment/ParticipantsinfoAPI",
    VOLUNTEER_BY_PARTICIPANT: "/api/eRecruitment/VolunteerByParticipant",
    TEMPLATE_FOR_COMMUNICATION: "/api/eRecruitment/TemplateForCommunication",
    SAVE_APPOINTMENT_PAYMENT: "/api/eRecruitment/SaveAppointmentPayment",
    SEND_PAYMENT_EMAIL: "/api/eRecruitment/SendPaymentEmail",
    SEND_CODE: "/api/eRecruitment/SendCode",
    GET_VOLUNTEER_AUDIT_LOG: "/api/eRecruitment/GetVolunteerAuditLog",
  },

  /** UserLogin endpoints used by video consultation feedback */
  USER_LOGIN: {
    VIRTUAL_CONSULTATION: "/api/UserLogin/VirtualConsultation",
    SAVE_FEEDBACK: "/api/UserLogin/SaveFeedback",
  },

  // ISF endpoints
  ISF: {
    GET_ALL: "/api/ISF/GetAll",
    GET_BY_ID: "/api/ISF/GetById",
    CREATE: "/api/ISF/Create",
    UPDATE: "/api/ISF/Update",
    DELETE: "/api/ISF/Delete",
    TEMPLATE_LIST: "/api/eISF/GetTemplateListData",
    GET_TEMPLATE_BY_ID: "/api/eISF/SaveTemplates",
    GETSTUDYLIST: "/api/eSchedule/ScheduleStudyDetails",
    GET_STUDY_FOLDERS: "/api/eISF/GetStudyFolderDocuments",
    SAVE_FOLDER_DOCUMENTS: "/api/eISF/SaveFolderDocuments",
    FILE_DELETE_LOCK: "/api/eISF/DeleteFolder",
    FILE_COMMENTS: "/api/eISF/FileComment",
    COMMENT_MENTION_CANDIDATES: "/api/eISF/CommentMentionCandidates",
    MY_TAGGED_COMMENTS: "/api/eISF/MyTaggedComments",
    FILE_FOLDER_MOVE: "/api/eISf/MoveDocumentToFolder",
    FILE_SAVE_SIGN: "/api/eISf/SaveFileSignature",
    FILE_FOLDER_RESTORE: "/api/eISF/RestoreFolder",
    FOLDER_DELETE: "/api/eISF/DeleteFolder",
    GET_DASHBOARD_DATA: "/api/eISF/GetDashboardData",
    //GET_AUDIT_LOGS: "/api/eISF/GetAuditLogs",
    GET_ISF_EVENT_LOG: "/api/eISF/GetEventLogDetails",
    GET_DOWNLOAD_AUDIT_LOGS: "/api/eISF/GetEventLogDetails",
    REQUEST_DOWNLOAD_AUDIT_LOG: "/api/eISF/GetEventLogDetails",
    GENERATE_CERTIFICATION: "/api/eISF/GenerateCertification",
    PLACEHOLDER_ACTIVITY: "/api/eISF/PlaceholderActivity",
    MILESTONE_ACTIVITY: "/api/eISF/MilestoneActivity",
    STUDY_SITE_CONFIG: "/api/eISF/StudySiteConfig",
    STUDY_REFERENCE_DATE: "/api/eISF/StudyReferenceDate",
    STUDY_MILESTONE: "/api/eISF/StudyMilestone",
    STUDY_GATE: "/api/eISF/StudyGate",
    EISF_DEFAULT_FOLDER: "/api/eISF/EISFDefaultFolder",
    DOCUMENT_TYPE_ACTIVITY: "/api/eISF/DocumentTypeActivity",
    DOCUMENT_CATEGORY_ACTIVITY: "/api/eISF/DocumentCategoryActivity",
    CONFIG_GET: "/api/eISF/GetConfig",
    CONFIG_SAVE: "/api/eISF/SaveConfig",
  },

  // File management endpoints
  FILES: {
    UPLOAD: "/api/Files/Upload",
    DOWNLOAD: "/api/Files/Download",
    DELETE: "/api/Files/Delete",
  },

  /**
   * CAPA (corrective / preventive action).
   * Uncomment usages in capaService.js when backend endpoints are live.
   */
  CAPA: {
    SAVE: "/api/CAPA/Save",
    UPDATE: "/api/CAPA/Update",
    REMOVE: "/api/CAPA/Remove",
    TRANSITION_STATUS: "/api/CAPA/TransitionStatus",
    LIST: "/api/CAPA/GetList",
    GET_BY_ID: "/api/CAPA/GetById",
    DASHBOARD: "/api/CAPA/GetDashboard",
    ACTION_ITEMS_LIST: "/api/CAPA/GetActionItems",
    SAVE_ACTION_ITEM: "/api/CAPA/SaveActionItem",
    UPDATE_ACTION_ITEM: "/api/CAPA/UpdateActionItem",
    DELETE_ACTION_ITEM: "/api/CAPA/DeleteActionItem",
    GET_APPROVER_RECORDS: "/api/CAPA/GetApproverRecordsByCapa",
    INSERT_APPROVER_RECORD: "/api/CAPA/InsertApproverRecord",
    UPDATE_APPROVER_RECORD: "/api/CAPA/UpdateApproverRecordStatus",
    DELETE_APPROVER_RECORD: "/api/CAPA/DeleteApproverRecord",
    TASKS_LIST: "/api/CAPA/GetTasks",
    APPROVALS_QUEUE: "/api/CAPA/GetApprovalsQueue",
    EFFECTIVENESS_QUEUE: "/api/CAPA/GetEffectivenessQueue",
    AUDIT_LOG: "/api/CAPA/GetAuditLog",
    CONFIG_GET: "/api/CAPA/GetConfig",
    CONFIG_SAVE: "/api/CAPA/SaveConfig",
    IMPLEMENTATION_TASKS_LIST: "/api/CAPA/GetImplementationTasksByCapa",
    SAVE_IMPLEMENTATION_TASK: "/api/CAPA/SaveImplementationTask",
    UPDATE_IMPLEMENTATION_TASK: "/api/CAPA/UpdateImplementationTaskStatus",
    DELETE_IMPLEMENTATION_TASK: "/api/CAPA/DeleteImplementationTask",
    VERIFICATION_REQUESTS_LIST: "/api/CAPA/GetVerificationRequestsByCapa",
    SAVE_VERIFICATION_REQUEST: "/api/CAPA/SaveVerificationRequest",
    SUBMIT_VERIFICATION_REQUEST: "/api/CAPA/SubmitVerificationRequest",
    DELETE_VERIFICATION_REQUEST: "/api/CAPA/DeleteVerificationRequest",
    REPORTS_EXPORT: "/api/CAPA/ExportReport",
  },

  // Analytics endpoints
  ANALYTICS: {
    DASHBOARD: "/api/Analytics/Dashboard",
    REPORTS: "/api/Analytics/Reports",
    EXPORT: "/api/Analytics/Export",
  },

  // Timeline / Page visit analytics (logged user + organization). Enable by setting real backend URLs.
  TIMELINE_ANALYTICS: {
    GET: "/api/TimelineAnalytics/Get",   // GET: userId, organizationId → { recentVisits, pageStats }
    SAVE: "/api/TimelineAnalytics/Save", // POST: { userId, organizationId, recentVisits, pageStats }
  },
};

export default API_ENDPOINTS;
